import { collection, doc, getDocs, query, where, addDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { CreateShippingAddressDTO, ShippingAddress } from '../types/shipping';

class ShippingAddressService {
  private collection = 'shipping_addresses';

  async findExistingAddress(userId: string, data: CreateShippingAddressDTO): Promise<string | null> {
    try {
      const addressesRef = collection(db, this.collection);
      const q = query(
        addressesRef,
        where('userId', '==', userId),
        where('type', '==', data.type),
        where('name', '==', data.name),
        where('address', '==', data.address)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].id;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  async listAddresses(userId: string, type?: 'sender' | 'recipient'): Promise<ShippingAddress[]> {
    try {
      const addressesRef = collection(db, this.collection);
      let q = query(addressesRef, where('userId', '==', userId));
      
      if (type) {
        q = query(q, where('type', '==', type));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data.type,
          name: data.name,
          address: data.address,
          city: data.city,
          zip_code: data.zip_code,
          country: data.country,
          phone: data.phone,
          userId: data.userId,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          attn: data.attn
        } as ShippingAddress;
      });
    } catch (error) {
      return [];
    }
  }

  async saveAddress(userId: string, data: CreateShippingAddressDTO): Promise<ShippingAddress> {
    try {
      // Verifica se já existe um endereço igual
      const existingId = await this.findExistingAddress(userId, data);
      
      if (existingId) {
        // Atualiza o endereço existente
        const now = new Date().toISOString();
        await this.updateAddress(existingId, { ...data, updatedAt: now });
        
        // Retorna o endereço atualizado
        const updatedDoc = await getDoc(doc(db, this.collection, existingId));
        const updatedData = updatedDoc.data();
        return {
          id: existingId,
          ...updatedData,
          updatedAt: now
        } as ShippingAddress;
      }
      
      // Cria um novo endereço
      const addressesRef = collection(db, this.collection);
      const now = new Date().toISOString();
      
      const docRef = await addDoc(addressesRef, {
        ...data,
        userId,
        createdAt: now,
        updatedAt: now
      });

      return {
        id: docRef.id,
        userId,
        createdAt: now,
        updatedAt: now,
        ...data
      };
    } catch (error) {
      throw new Error('Erro ao salvar endereço');
    }
  }

  async updateAddress(addressId: string, data: Partial<CreateShippingAddressDTO>): Promise<void> {
    try {
      const addressRef = doc(db, this.collection, addressId);
      await updateDoc(addressRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      throw new Error('Erro ao atualizar endereço');
    }
  }
}

export const shippingAddressService = new ShippingAddressService();
